import { probeClearAll, probeGetConnectionTree } from 'js/actions';
import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import { CustomUserConfirmation } from 'js/components/route_utils/UserConfirmation';
import { PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
} from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_CDC } from 'js/constants/formTypes';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useChangeDetection } from 'new/components/flows/hooks/useChangeDetection';
import useCDC from 'new/components/flows/types/cdc/CreateCDCFlow/CDCFlow/useCDC';
import {
  mapToSinkConfig,
  parseSinkConfig,
} from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/sinkConfigMapper';
import {
  mapToSource,
  parseSourceConfig,
} from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/sourceConfigMapper';
import useInitialSetupPage from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/useInitialSetupPage';
import useResourceCreation from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/useResourceCreation';
import flowTypes from 'new/components/flows/types/flowTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { usePagesRef } from 'new/elements/Form/Form';
import { FCFormPageType, useForm } from 'new/elements/Form/useForm';
import { Stack } from 'new/elements/Stack';
import { DatabaseSetup } from 'new/elements/flows/DatabaseSetup';
import useProbeTree from 'new/hooks/adapters/useProbeTree';
import usePaging from 'new/state/paging/repository/usePaging';
import useRequests from 'new/state/request/repository/useRequests';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useCDCSaveConfirmation } from '../hooks/useCDCSaveConfirmation';
import { useCDCValidations } from './useCDCValidations';
import { useDataSourceRepository } from 'new/state/data_source/repository/useDataSourceRepository';

const parseResource = (config, type, allAvailableTables = []) => {
  if (type === 'SOURCE') return mapToSource(config, allAvailableTables);
  return mapToSinkConfig(config);
};

type ResourceSetupPageProps = {};
export const ResourceSetupPage: FCFormPageType<ResourceSetupPageProps> = () => {
  const {
    overrideNextFunction,
    setLoaderMessages,
    nextPage,
    setConfirmButtonTitle,
    setShowLeaveConfirmation,
    validate,
    setValidation,
  } = useForm();
  const pagesRef = usePagesRef();
  const cdcData = useCDC();
  const {
    connectionType,
    selectedConnection,
    source,
    sink,
    connectionCategory,
    nexset,
    sinkConfig,
    selectedBucket,
    allowCustomPrefixEditor,
    updateCDCResourceConfig,
    credentialId,
  } = cdcData;
  const { validateConfig } = useCDCValidations(cdcData);
  const { databases, error: probeError } = useProbeTree(credentialId);
  const allAvailableTables = useMemo(
    () => databases?.[0]?.children?.map((table) => table.name) || [],
    [databases],
  );

  const { onSinkConfigChanged, onBucketSelected } = useFlowsSetup(
    FORM_TYPE_CREATE_CDC,
    flowTypes.cdc,
  );
  const { showError } = useRequests();

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  const initialLoading = useInitialSetupPage();

  const { getMetaInfoById } = usePaging();
  const meta = getMetaInfoById(selectedConnection);
  const preventProbeTree = useRef(false);

  const editingId = (() => {
    if (connectionType === 'SOURCE' && !!source?.id) return source.id;
    if (connectionType === 'SINK' && !!sink?.id) return sink.id;
    return null;
  })();

  const { getDataSourceById } = useDataSourceRepository();

  const sourceBeingEditedId = connectionType === 'SOURCE' ? editingId : null;
  const dataSourceRef = useRef<any>(getDataSourceById(sourceBeingEditedId));

  useEffect(() => {
    dataSourceRef.current = getDataSourceById(sourceBeingEditedId);
  }, [sourceBeingEditedId, getDataSourceById]);

  const pendingAdvancedResourceConfigRef = useRef<any>(null);
  const saveResource = useResourceCreation(editingId, pendingAdvancedResourceConfigRef);

  // Save confirmation logic
  const {
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
  } = useCDCSaveConfirmation();

  // Get change detection at component level
  const { hasChanges: currentHasChanges } = useChangeDetection(FORM_TYPE_CREATE_CDC);

  const isLoadingAutoDetectData =
    connectionType === 'SOURCE' &&
    source &&
    source.source_config?.['cdc.table.config']?.['selection.mode'] === 'semi_auto' &&
    !allAvailableTables?.length &&
    !probeError;

  const getTrees = () => {
    if (!initialLoading && credentialId && !preventProbeTree.current) {
      setLoaderMessages({
        [PROBE_GET_TREE_REQUEST]: 'Connecting to ' + meta?.name,
      });
      if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
        dispatch(probeGetConnectionTree(credentialId));
        const sourceCredentialId =
          typeof source?.data_credentials === 'number'
            ? source?.data_credentials
            : source?.data_credentials?.id || source?.data_credentials_id;
        if (sourceCredentialId) {
          dispatch(probeGetConnectionTree(sourceCredentialId));
        }
      }
      setLoaderMessages({});
    }
  };

  useEffect(() => {
    return () => {
      dispatch(probeClearAll());
    };
  }, []);

  useEffect(() => {
    if (!sink && connectionType === 'SINK' && source) {
      setConfirmButtonTitle('Done');
      return;
    }
    if ((sink && connectionType === 'SINK') || (source && connectionType === 'SOURCE')) {
      setConfirmButtonTitle('Save');
    }
  }, [sink, source, connectionType]);

  useEffect(() => {
    getTrees();
  }, [initialLoading, credentialId, source]);

  useEffect(() => {
    setValidation(validateConfig);
  }, [validateConfig]);

  useEffect(() => {
    overrideNextFunction(async () => {
      // TODO: This is a hack to prevent probe/tree from being fetched when the source
      // is saved. The hack saves us from needing to refactor and simplify the
      // implementation of the CDC form.
      preventProbeTree.current = true;

      // Check if we should show save confirmation

      // For CDC flows, we need to check if we have a source being edited
      const sourceForCheck = sourceBeingEditedId
        ? {
            ...dataSourceRef.current,
            id: sourceBeingEditedId,
            status: 'ACTIVE',
          }
        : null;

      const isSourceActive = sourceForCheck?.status === 'ACTIVE';
      const supportsRunAction = shouldRenderRunAction(sourceForCheck, connectionCategory);

      const shouldShowSaveConfirmation = !!(
        connectionType === 'SOURCE' &&
        isSourceActive &&
        supportsRunAction &&
        currentHasChanges && // Only show confirmation if there are actual changes
        !isSaving
      );

      if (shouldShowSaveConfirmation) {
        setShowSaveConfirmation(true);
        return new Promise<boolean>((resolve) => {
          pendingSaveRef.current = async (runWithIngestion: boolean) => {
            const valid = await validate();
            if (!valid) {
              resolve(false);
              return;
            }
            const result = await saveResource(runWithIngestion);
            if (result) {
              setShowLeaveConfirmation(false);
            }
            resolve(result);
            return result;
          };
        });
      } else {
        try {
          const valid = await validate();
          if (!valid) return false;
          const result = await saveResource(currentRunWithIngestion);
          if (result) {
            setShowLeaveConfirmation(false);
          }
          return result === true; // Ensure we return a boolean
        } catch (error) {
          return false;
        }
      }
    });
    return () => {
      overrideNextFunction(null);
    };
  }, [
    saveResource,
    currentRunWithIngestion,
    currentHasChanges,
    isSaving,
    setShowSaveConfirmation,
    pendingSaveRef,
    setShowLeaveConfirmation,
    sourceBeingEditedId,
    connectionCategory,
    getDataSourceById,
    overrideNextFunction,
  ]);

  const onConfigUpdated = (resourceConfig) => {
    updateCDCResourceConfig({
      resourceConfig: parseResource(resourceConfig, connectionType, allAvailableTables),
    });
  };

  const onRequestProbeTree = useCallback(
    (node) => {
      dispatch(probeGetConnectionTree(credentialId, false, node));
    },
    [credentialId],
  );

  const initialResourceConfig = useMemo(() => {
    if (!sink && !source) {
      return undefined;
    }

    if (sink) {
      return parseSinkConfig(sink);
    }

    if (connectionType === 'SOURCE' && source) {
      // if "auto-detect new tables" is enabled, wait for the tables to be loaded
      if (isLoadingAutoDetectData) {
        return undefined;
      }

      return parseSourceConfig(source, allAvailableTables);
    }

    // For new sink creation, use the sinkConfig from form data
    if (connectionType === 'SINK' && sinkConfig) {
      return {
        name: sinkConfig.name || '',
        description: sinkConfig.description || '',
        namespace: sinkConfig.database || sinkConfig.namespace || '',
        schemaMode: sinkConfig.mapping?.mode !== 'auto' ? 'name' : 'auto',
        captureDelete: sinkConfig.mapping?.replay_row_deletions || false,
        tablePrefix: sinkConfig.mapping?.table_name_prefix || '',
        tableSuffix: sinkConfig.mapping?.table_name_suffix || '',
        trackerEnabled: sinkConfig.mapping?.tracker_mode === 'RECORD' || false,
        trackerName: sinkConfig.mapping?.tracker_name || '',
        mapping: sinkConfig.mapping?.overridden_mappings || {},
        selection: {},
      };
    }
    return undefined;
  }, [sink, source, connectionType, sinkConfig, isLoadingAutoDetectData]);

  const onDiscardAdvancedForm = () => {
    setShowLeaveConfirmation(true);
  };

  const onSubmitAdvancedForm = (resourceConfig) => {
    const newInfo = parseResource(resourceConfig, connectionType, allAvailableTables);
    pendingAdvancedResourceConfigRef.current = {
      resourceConfig: newInfo,
    };
    updateCDCResourceConfig({
      resourceConfig: newInfo,
    });
    nextPage();
  };

  const onSaveAdvancedMode = async (newSinkConfig: any) => {
    onSinkConfigChanged(newSinkConfig, null, null);
  };

  if (initialLoading || isLoadingAutoDetectData)
    return (
      <Stack padding="50px">
        <CenteredLoader />
      </Stack>
    );

  // Render main content
  let mainContent;
  if (meta?.connectionCategory === CONNECTION_CATEGORY_FILE) {
    mainContent = (
      <FileSystem
        sink={sink}
        nexset={nexset}
        connectionType={connectionType}
        selectedBucket={selectedBucket}
        allowCustomPrefixEditor={allowCustomPrefixEditor}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        onBucketSelected={onBucketSelected}
        flowType={flowTypes.cdc}
        sinkConfig={sinkConfig}
        onSaveAdvancedMode={onSaveAdvancedMode}
        onSinkConfigChanged={onSinkConfigChanged}
        nextFormStage={nextPage}
        onError={showError}
      />
    );
  } else {
    mainContent = (
      <DatabaseSetup
        source={source}
        sink={sink}
        nexset={nexset}
        parentScrollRef={pagesRef}
        resourceConfig={initialResourceConfig}
        onConfigUpdated={onConfigUpdated}
        onRequestProbeTree={onRequestProbeTree}
        credentialId={credentialId}
        connectorType={selectedConnection}
        resourceType={connectionType}
        onDiscardAdvancedForm={onDiscardAdvancedForm}
        onSubmitAdvancedForm={onSubmitAdvancedForm}
      />
    );
  }

  // Render SaveConfirmation modal alongside main content
  if (showSaveConfirmation) {
    return (
      <>
        {mainContent}
        <CustomUserConfirmation
          title="Save Changes to Active Source"
          message="You've made changes to an active source. Choose how you want to apply these changes:"
          okButtonLabel="Save for Next Run"
          cancelButtonLabel="Cancel"
          onConfirm={() => handleSaveConfirmation(true)}
          onClose={onCloseSaveConfirmation}
          yesWithChangesButtonLabel="Save & Run Now"
          onRunWithIngestion={() => handleSaveConfirmation(false)}
        />
      </>
    );
  }

  return mainContent;
};

ResourceSetupPage.formConfig = {
  title: 'Configure',
};
